import { inject, Injectable } from '@angular/core';
import { UserContextRestService } from './rest/user-context.rest.service';
import { Store } from '@ngrx/store';
import { RootState } from '../store';
import { LoadOnceService } from './load-once.service';
import { UserContextLoadAction, UserContextLoadFailAction, UserContextLoadSuccessAction } from '../store/session/user-context/user-context.actions';
import { ClearSelectedCustomerAction } from '../store/session/selected-customer/select-customer.actions';
import { goAction } from '../store/router/router.actions';
import { appSettings } from '../../appSettings';
import { AuthenticationService } from '../authentication/authentication.service';
import { filter, first } from 'rxjs/operators';

@Injectable({ providedIn: 'root' })
export class UserContextService {

  private authenticationService = inject(AuthenticationService);

  constructor(
    private store: Store<RootState>,
    private userContextRestService: UserContextRestService,
    private loadOnceService: LoadOnceService,
  ) {
    this.authenticationService.isAuthenticated$().pipe(
      filter(isAuthenticated => !!isAuthenticated),
      first()
    ).subscribe(() => this.loadOnce())
  }

  async loadOnce(): Promise<void> {
    await this.loadOnceService.loadOnce(
      'userContext',
      () => this.loadUserContext(),
      this.store.select(state => state.session.userContext.contextLoading),
      this.store.select(state => state.session.userContext.contextLoaded),
      this.store.select(state => state.session.userContext.contextFailed),
    )
  }

  private async loadUserContext() {
    this.store.dispatch(new UserContextLoadAction());
    try {
      const userContext = await this.userContextRestService.getUserContext();
      this.store.dispatch(new UserContextLoadSuccessAction(userContext));
      localStorage.removeItem('retry-login');

      if (userContext.activeCustomerId && userContext.customers && !userContext.customers[userContext.activeCustomerId]) {
        this.store.dispatch(new ClearSelectedCustomerAction());
        this.store.dispatch(goAction({ path: appSettings.SELECT_CUSTOMER_ROUTE }));
      }
    } catch (error) {
      if (error.status === 401) {
        if (localStorage.getItem('retry-login') !== 'true') {
          localStorage.clear();
          localStorage.setItem('retry-login', 'true');
          this.authenticationService.login();
          return;
        }
      }
      this.store.dispatch(new UserContextLoadFailAction(error));
    }
  }
}
